<template>
    <div class="price-section">
        <Translation
            class="price-section-title"
            element="div"
            :translate="{
                key: 'shared.baggage',
            }" />

        <div class="price-section-rows">
            <template v-for="(item, index) in selected" :key="index">
                <template v-for="bag in item.baggages" :key="bag?.Id">
                    <div v-if="showPriceBreakdown(bag)">
                        <PriceSpecificationRow
                            v-if="bag.PriceBreakdown.BaggagePrice"
                            :price="bag.PriceBreakdown.BaggagePrice"
                            :description="
                                getBaggageLabel(bag, item.pax.AdditionalBaggage.SplitSector)
                            " />

                        <PriceSpecificationRow
                            v-if="bag.PriceBreakdown.BookingFee"
                            class="sub"
                            :price="bag.PriceBreakdown.BookingFee"
                            :description="t({ key: 'checkout.booking_fee' })" />
                    </div>
                    <PriceSpecificationRow
                        v-else
                        :price="bag.Price"
                        :description="
                            getBaggageLabel(bag, item.pax.AdditionalBaggage.SplitSector)
                        " />
                </template>
            </template>
            <PriceSpecificationRow
                :price="totalPrice"
                class="total"
                :description="t({ key: 'checkout.total_price_baggage' })" />
        </div>
        <Divider color="lightGrey" bottomMargin="xl" topMargin="xl" thickness="1" />
    </div>
</template>

<script lang="ts">
    import { t } from '@shared/helpers/translation';
    import { isNil } from 'lodash';
    import { computed, defineComponent, PropType } from 'vue';
    import { FlightPax, FlightProduct } from '@/api/Cart/cart.interfaces';
    import { Money } from '@libs/money/money.interfaces';
    import { Baggage } from '@shared/interfaces/baggage';
    import { getSelectedBaggages, getBaggageLabel } from '@shared/helpers/baggage';
    import PriceSpecificationRow from '../PriceSpecificationRow.vue';
    import Translation from '@ui/components/Translation/Translation.vue';
    import Divider from '@ui/components/Divider/Divider.vue';

    interface PaxBaggage {
        baggages: Baggage[];
        pax: FlightPax;
    }

    export default defineComponent({
        name: 'PriceSpecificationBags',
        methods: { t, getBaggageLabel },
        components: {
            Divider,
            PriceSpecificationRow,
            Translation,
        },
        props: {
            product: {
                type: Object as PropType<FlightProduct>,
                required: true,
            },
        },
        setup({ product }) {
            const selected = computed((): PaxBaggage[] =>
                product.Pax.map((pax: FlightPax) => ({
                    baggages: getSelectedBaggages(pax.AdditionalBaggage),
                    pax,
                }))
            );

            const allBaggages = computed((): Baggage[] =>
                selected.value.map((x) => x.baggages).flatMap((x) => x)
            );

            const totalPrice = computed(() =>
                allBaggages.value.reduce(
                    (sum: Money, bag: Baggage | null) => {
                        return {
                            Amount: (bag?.Price.Amount ?? 0) + sum.Amount,
                            Currency: bag?.Price.Currency ?? sum.Currency,
                        };
                    },
                    { Amount: 0, Currency: '' }
                )
            );

            const showPriceBreakdown = (bag: Baggage): boolean => {
                return (
                    bag.PriceBreakdown &&
                    (!isNil(bag.PriceBreakdown.BaggagePrice) ||
                        !isNil(bag.PriceBreakdown.BookingFee))
                );
            };

            return {
                totalPrice,
                selected,
                showPriceBreakdown,
            };
        },
    });
</script>
